import { Service, Testimonial, Stat, FAQ } from '@/lib/index';
import { IMAGES } from '@/assets/images';

export const services: Service[] = [
  {
    id: 'credit-repair',
    title: 'Complete Credit Transformation',
    description: 'More than just scanning reports - get a complete credit roadmap with education, hacks, and strategies to qualify for homes, cars, credit cards, and business funding.',
    icon: 'TrendingUp',
    features: [
      'Deep credit report analysis across all 3 bureaus (Equifax, Experian, TransUnion)',
      'AI-powered dispute letter generation with FCRA compliance',
      'Credit line increase strategies and timing optimization',
      'Personalized roadmap to buying a house (mortgage qualification)',
      'Car loan optimization strategies (0% interest hacks)',
      'Credit card approval strategies (rewards maximization)',
      'Business funding preparation (EIN credit building)',
      'Credit education library with tips, hacks, and insider secrets',
      'Debt-to-income ratio optimization for loan approvals',
      'Credit utilization management (30% rule and beyond)',
      'Authorized user strategies for rapid score boosts',
      'Collections removal and negotiation tactics',
    ],
    aiCapabilities: [
      'AI scans for errors 10x faster than manual review',
      'Predictive modeling shows your credit score in 3, 6, 12 months',
      'Smart dispute timing based on bureau response patterns',
      'Automated tracking of all 3 bureaus with instant alerts',
      'Personalized action plans based on your specific goals',
      'Real-time credit monitoring with score change notifications',
    ],
    image: IMAGES.CREDIT_REPAIR_1,
  },
  {
    id: 'tax-services',
    title: 'Elite Tax Strategy & Preparation',
    description: 'Way more than your average accountant - we find hidden deductions, optimize entity structures, and use advanced strategies to save you thousands.',
    icon: 'Calculator',
    features: [
      'Individual tax preparation (1040, Schedule C, itemized deductions)',
      'Business tax filing (LLC, S-Corp, C-Corp, Partnership)',
      'Entity structure optimization (LLC vs S-Corp vs C-Corp analysis)',
      'Augusta Rule strategy (rent your home to your business)',
      'Home office deduction maximization',
      'Vehicle expense optimization (standard vs actual)',
      'Retirement account strategies (SEP IRA, Solo 401k)',
      'Quarterly estimated tax planning and payments',
      'Multi-state tax filing and nexus analysis',
      'IRS audit representation and defense',
      'Tax amendment and prior year corrections',
      'Bookkeeping and financial statement preparation',
    ],
    aiCapabilities: [
      'AI scans receipts and finds $1000s in missed deductions',
      'Automated expense categorization and tracking',
      'Real-time tax law updates (2026 OBBBA compliance)',
      'Predictive tax liability forecasting',
      'Entity comparison calculator (shows exact savings)',
      'Deduction opportunity alerts based on your business type',
    ],
    image: IMAGES.TAX_SERVICES_1,
  },
  {
    id: 'business-consulting',
    title: 'Business Funding & Growth Strategy',
    description: 'Get your business funded and scaled - from formation to $50k+ credit lines, SBA loans, and strategic growth planning.',
    icon: 'Briefcase',
    features: [
      'Business formation (LLC, S-Corp, C-Corp setup)',
      'EIN credit building strategies (separate from personal credit)',
      'Business funding roadmap ($10k to $250k+)',
      'Tier-1 trade line strategies (Net-30, Net-60 accounts)',
      'Business credit card strategies (Chase Ink, Amex Business)',
      'SBA loan preparation and application support',
      'Business line of credit qualification strategies',
      'NAICS code optimization for funding approval',
      'Financial statement preparation for lenders',
      'Business plan development and pitch deck creation',
      'Cash flow management and forecasting',
      'Growth strategy and scaling roadmaps',
    ],
    aiCapabilities: [
      'AI matches you with best funding sources (88%+ approval odds)',
      'Automated business credit monitoring',
      'Predictive funding approval calculator',
      'Market opportunity analysis for your industry',
      'Competitive intelligence and positioning',
      'Real-time KPI tracking and performance alerts',
    ],
    image: IMAGES.CONSULTING_1,
  },
];

export const testimonials: Testimonial[] = [
  {
    id: 'testimonial-1',
    name: 'Marcus Johnson',
    role: 'Small Business Owner',
    company: 'Johnson Construction LLC',
    content: 'The credit repair service was life-changing. My score went from 580 to 720 in just 8 months. The AI analysis found errors I never would have caught, and now I qualified for a business loan that transformed my company.',
    rating: 5,
    image: IMAGES.HERO_BG_1,
  },
  {
    id: 'testimonial-2',
    name: 'Sarah Chen',
    role: 'Freelance Designer',
    company: 'Chen Creative Studio',
    content: 'Tax season used to be my nightmare. Now with their AI-powered service, I discovered $8,000 in deductions I was missing. The team is professional, responsive, and genuinely cares about maximizing my refund.',
    rating: 5,
    image: IMAGES.HERO_BG_2,
  },
  {
    id: 'testimonial-3',
    name: 'David Martinez',
    role: 'CEO',
    company: 'TechStart Innovations',
    content: 'The business consulting service helped us scale from $500K to $2M in revenue in 18 months. Their AI-driven market analysis gave us insights our competitors are still missing. Absolutely worth every penny.',
    rating: 5,
    image: IMAGES.HERO_BG_3,
  },
  {
    id: 'testimonial-4',
    name: 'Jennifer Williams',
    role: 'Real Estate Agent',
    company: 'Williams Realty Group',
    content: 'I was skeptical about AI in financial services, but the results speak for themselves. My credit score improved by 140 points, and the personalized guidance was better than any human-only service I tried before.',
    rating: 5,
    image: IMAGES.HERO_BG_4,
  },
  {
    id: 'testimonial-5',
    name: 'Robert Thompson',
    role: 'Restaurant Owner',
    company: 'Thompson Family Diner',
    content: 'The tax planning saved me over $15,000 last year. The AI caught deductions my previous accountant missed for three years. Plus, the business consulting helped me optimize my operations and increase profit margins by 22%.',
    rating: 5,
    image: IMAGES.HERO_BG_5,
  },
  {
    id: 'testimonial-6',
    name: 'Amanda Foster',
    role: 'E-commerce Entrepreneur',
    company: 'Foster Fashion Co',
    content: 'This team is the real deal. They helped me repair my credit after a rough divorce, handled my complex multi-state taxes, and gave me the business strategy that doubled my online sales. I recommend them to everyone.',
    rating: 5,
    image: IMAGES.HERO_BG_6,
  },
];

export const stats: Stat[] = [
  {
    id: 'stat-1',
    value: '2,500+',
    label: 'Clients Served',
    icon: 'Users',
    description: 'Trusted by thousands of individuals and businesses nationwide',
  },
  {
    id: 'stat-2',
    value: '127',
    label: 'Average Score Increase',
    icon: 'TrendingUp',
    description: 'Average credit score improvement within 6-12 months',
  },
  {
    id: 'stat-3',
    value: '$12M+',
    label: 'Tax Savings Generated',
    icon: 'DollarSign',
    description: 'Total tax savings and refunds secured for our clients',
  },
  {
    id: 'stat-4',
    value: '98%',
    label: 'Client Satisfaction',
    icon: 'Award',
    description: 'Clients who would recommend our services to others',
  },
];

export const faqs: FAQ[] = [
  {
    id: 'faq-1',
    question: 'How does AI improve credit repair services?',
    answer: 'Our AI system analyzes your credit reports 24/7, identifying errors, inaccuracies, and opportunities for improvement that human review might miss. It processes thousands of data points to create personalized dispute strategies, predicts the best timing for actions, and continuously monitors your progress. This results in faster results and higher success rates compared to traditional methods.',
  },
  {
    id: 'faq-2',
    question: 'What makes your tax services different from traditional accountants?',
    answer: 'We combine expert human tax professionals with AI-powered analysis. Our AI scans tax code updates in real-time, identifies deductions specific to your situation, and ensures compliance across all jurisdictions. This hybrid approach means you get the personal touch of a dedicated tax professional backed by technology that never misses a detail or deadline.',
  },
  {
    id: 'faq-3',
    question: 'How long does credit repair typically take?',
    answer: 'Most clients see initial improvements within 30-60 days, with significant score increases (50-150 points) occurring within 6-12 months. The timeline depends on your starting credit profile, the number of items requiring dispute, and how quickly credit bureaus respond. Our AI system optimizes the process to achieve results as quickly as legally possible.',
  },
  {
    id: 'faq-4',
    question: 'Do you offer services for both individuals and businesses?',
    answer: 'Yes! We serve individuals, sole proprietors, LLCs, S-Corps, C-Corps, and partnerships. Our business consulting services are tailored to your company size and industry, whether you\'re a startup seeking structure or an established business looking to scale. We handle everything from business formation to multi-million dollar tax strategies.',
  },
  {
    id: 'faq-5',
    question: 'Is my financial information secure with AI processing?',
    answer: 'Absolutely. We use bank-level encryption (256-bit SSL), comply with all financial data protection regulations (including SOC 2 Type II), and our AI systems are hosted on secure, HIPAA-compliant servers. Your data is never sold or shared with third parties. We take security as seriously as we take your financial success.',
  },
  {
    id: 'faq-6',
    question: 'What are your pricing and payment options?',
    answer: 'We offer flexible pricing based on service complexity and your needs. Credit repair starts at $99/month, tax preparation ranges from $200-$1,500 depending on complexity, and business consulting is customized per engagement. We offer free initial consultations to assess your situation and provide transparent pricing. Payment plans are available for larger projects.',
  },
  {
    id: 'faq-7',
    question: 'Can you help if I\'ve been denied credit or audited by the IRS?',
    answer: 'Yes, these are situations where our expertise truly shines. For credit denials, we analyze the reasons, develop a targeted improvement plan, and work to position you for approval. For IRS audits, we provide full representation, document preparation, and negotiation services. Our team has successfully resolved hundreds of audit cases with favorable outcomes.',
  },
  {
    id: 'faq-8',
    question: 'Do I need to be tech-savvy to use your AI-powered services?',
    answer: 'Not at all! While our backend uses sophisticated AI, our client interface is designed to be simple and intuitive. You\'ll have a dedicated human advisor who guides you through everything. The AI works behind the scenes to enhance your results, but you\'ll always have personal support whenever you need it. Think of it as having a super-smart assistant that makes your advisor even better.',
  },
];
